import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Card, CardContent, Typography, CircularProgress, IconButton, Tooltip,
  Select, MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import html2canvas from 'html2canvas';
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, Brush, AreaChart, ReferenceLine,
} from 'recharts';
import { getScoreColor, colors } from '../../theme';

/**
 * MarketPsychologySection
 *
 * Renders the experimental 8-state Market Psychology signal (backend:
 * backend/services/psychology/). Pure renderer — all thresholds, state
 * labels, and score levels come from the API response; this component only
 * maps backend-supplied `*_level` keys to theme colors and the fixed
 * `psychology_state` enum to a display color/label.
 *
 * Layout mirrors BehaviorInsights/VNIndexCorrelationChart (inline header
 * stats, no boxed metric cards) so the section uses its full width.
 */

// Fixed enum -> display color mapping (not a threshold derivation from a raw score).
const STATE_STYLES = {
  PANIC:         { color: '#b71c1c' },
  DESPAIR:       { color: '#c62828' },
  FEAR:          { color: '#e65100' },
  DOUBT:         { color: '#616161' },
  HOPE:          { color: '#1565c0' },
  OPTIMISM:      { color: '#2e7d32' },
  EUPHORIA:      { color: '#6a1b9a' },
  EXTREME_GREED: { color: '#ad1457' },
};

// Options passed as-is to the backend /history limit + /stats days (labels: sentiment.psychology.range.*).
const TIMEFRAME_OPTIONS = [
  { value: '1M', days: 30 },
  { value: '2M', days: 60 },
  { value: '3M', days: 90 },
  { value: '6M', days: 180 },
  { value: '1Y', days: 280 },
  { value: '2Y', days: 560 },
  { value: '3Y', days: 850 },
  { value: '5Y', days: 1400 },
  { value: 'ALL', days: 1500 },
];

// action_bias is a backend-computed enum (built only from validated signals) -> fixed theme level, not a raw-score threshold.
const ACTION_BIAS_LEVELS = {
  ACCUMULATE: 'excellent',
  REDUCE_EXPOSURE: 'poor',
  WAIT_FOR_CONFIRMATION: 'fair',
  HOLD_CAUTIOUS: 'fair',
  NEUTRAL: 'unknown',
};

// transition_direction is a backend-computed enum (closer to which neighboring state) -> fixed theme level, not a raw-score threshold.
const TRANSITION_DIRECTION_LEVELS = {
  up: 'good',
  down: 'poor',
};

// key_drivers' direction is a backend-computed enum (bullish/bearish/neutral composite reading) -> fixed theme level.
const KEY_DRIVER_LEVELS = {
  bullish: 'good',
  bearish: 'poor',
  neutral: 'unknown',
};
const KEY_DRIVER_ARROWS = {
  bullish: ' \u2191',
  bearish: ' \u2193',
  neutral: '',
};

const getVnindexDomain = (points) => {
  const values = points.map((point) => point.vnindex).filter((value) => value != null);
  if (!values.length) return ['auto', 'auto'];
  const low = Math.min(...values);
  const high = Math.max(...values);
  const padding = Math.max((high - low) * 0.06, 20);
  return [Math.floor(low - padding), Math.ceil(high + padding)];
};

const SummaryItem = ({ label, value, color, tooltip }) => {
  const content = (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.15,
      minWidth: 0, flexShrink: 0,
    }}
    >
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
      <Typography sx={{ color, fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.1, whiteSpace: 'nowrap' }}>
        {value}
      </Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

SummaryItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  color: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
};

SummaryItem.defaultProps = {
  tooltip: null,
};

const formatScore = (value) => (value == null ? '—' : Number(value).toFixed(1));

const CHART_HEIGHT = 368;
const CHART_BRUSH_HEIGHT = 32;
const CHART_MARGIN = { top: 5, right: 8, left: 4, bottom: 6 };
const CHART_LEFT_AXIS_WIDTH = 42;
const CHART_RIGHT_AXIS_WIDTH = 36;
// Brush shows this many of the most recent points by default; drag its handles to pan back through history.
const CHART_DEFAULT_VISIBLE_POINTS = 90;

const DIRECTION_ARROWS = { up: ' \u2191', down: ' \u2193' };
const formatTransitionValue = (transitionProbability, transitionDirection) => (
  `${Math.round(transitionProbability * 100)}%${DIRECTION_ARROWS[transitionDirection] || ''}`
);

const MarketPsychologySection = ({
  indexCode, signal, stats, history, vnindexByDate, historyDays, onHistoryDaysChange,
  loading, refreshing, calculating, onCalculate, t,
}) => {
  const exportRef = useRef(null);
  const [exportingImage, setExportingImage] = useState(false);
  const handleExportImage = async () => {
    if (!exportRef.current || exportingImage) return;
    try {
      setExportingImage(true);
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        onclone: (clonedDoc) => {
          // html2canvas can't render MUI Select's internals correctly; swap in a plain-text stand-in for the capture
          clonedDoc.querySelectorAll('.export-range-label').forEach((el) => {
            el.style.display = 'flex';
          });
        },
      });
      const asOfDate = signal?.trade_date || new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `market-psychology-${indexCode}-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingImage(false);
    }
  };

  const stateKey = signal?.psychology_state;
  const stateStyle = STATE_STYLES[stateKey] || STATE_STYLES.DOUBT;
  const stateLabel = stateKey
    ? t(`sentiment.psychology.state.${stateKey}`, stateKey)
    : '—';
  const stateHint = stateKey ? t(`sentiment.psychology.stateHint.${stateKey}`, '') : '';

  const chartData = useMemo(() => (history || [])
    .slice()
    .reverse()
    .map((row) => {
      const tradeDate = String(row.trade_date).slice(0, 10);
      return {
        date: tradeDate,
        sentiment: row.sentiment_score,
        opportunity: row.opportunity_score,
        vnindex: vnindexByDate[tradeDate] ?? null,
      };
    }), [history, vnindexByDate]);

  const [brushRange, setBrushRange] = useState({ startIndex: 0, endIndex: 0 });
  useEffect(() => {
    const endIndex = Math.max(0, chartData.length - 1);
    setBrushRange({
      startIndex: Math.max(0, chartData.length - CHART_DEFAULT_VISIBLE_POINTS),
      endIndex,
    });
  }, [chartData.length, historyDays]);

  const visibleChartData = useMemo(() => {
    if (!chartData.length) return [];
    const start = Math.max(0, Math.min(brushRange.startIndex, brushRange.endIndex));
    const end = Math.min(chartData.length - 1, Math.max(brushRange.startIndex, brushRange.endIndex));
    return chartData.slice(start, end + 1);
  }, [brushRange.endIndex, brushRange.startIndex, chartData]);

  const vnindexDomain = getVnindexDomain(visibleChartData);
  const vnindexBrushDomain = getVnindexDomain(chartData);
  // Brush's mini-preview shares data via cloning from the outer chart's context, so an explicit
  // `data` prop or a <YAxis> on the inner AreaChart gets ignored/corrupts the main chart's axis
  // layout (recharts bug). Instead, shift values via a dataKey function so the default [0, auto]
  // domain still shows real fluctuation instead of a near-flat line.
  const vnindexPreviewBase = typeof vnindexBrushDomain[0] === 'number' ? vnindexBrushDomain[0] : 0;
  const vnindexLabel = t('sentiment.psychology.legendVnindex', 'VNINDEX');
  const sentimentLabel = t('sentiment.psychology.sentimentScore', 'Sentiment Score');
  const opportunityLabel = t('sentiment.psychology.opportunityScore', 'Opportunity Score');
  const sentimentColor = getScoreColor('good');
  const opportunityColor = getScoreColor('excellent');
  const chartGridStroke = '#ececec';
  const chartAxisTick = { fontSize: 10, fill: '#94a3b8' };
  const chartTooltipStyle = {
    fontSize: 11, borderRadius: 6, border: '1px solid #e2e8f0', padding: '6px 10px',
  };

  const handleBrushChange = (range) => {
    if (range?.startIndex == null || range?.endIndex == null) return;
    setBrushRange({ startIndex: range.startIndex, endIndex: range.endIndex });
  };
  const formatTooltipValue = (value, name) => {
    if (value == null) return ['—', name];
    if (name === vnindexLabel) return [`${Number(value).toFixed(2)} ₫`, name];
    return [Number(value).toFixed(1), name];
  };
  const formatDateTick = (value) => {
    const dt = new Date(value);
    return `${dt.getDate()}/${dt.getMonth() + 1}`;
  };
  const formatTooltipLabel = (value) => {
    const dt = new Date(value);
    return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
  };

  const actionBiasHintKey = signal?.action_bias_hint_key || signal?.action_bias;
  const actionBiasLabel = signal?.action_bias
    ? t(`sentiment.psychology.actionBias.${signal.action_bias}`, signal.action_bias)
    : null;

  const opportunitySourceKey = signal?.opportunity_source
    ? String(signal.opportunity_source).toLowerCase()
    : null;

  const exposureLabel = (
    signal?.recommended_exposure_min != null && signal?.recommended_exposure_max != null
  )
    ? `${Math.round(signal.recommended_exposure_min * 100)}–${Math.round(signal.recommended_exposure_max * 100)}%`
    : null;

  const warningCodes = signal?.warnings || [];

  const keyDrivers = signal?.key_drivers || [];

  const scoreLevelLabel = (levelKey) => t(`sentiment.psychology.scoreLevel.${levelKey || 'unknown'}`, levelKey || 'Unknown');
  const reliabilityHint = (tier) => {
    if (tier === 'experimental') {
      return t('sentiment.psychology.experimentalScoreHint', 'Chưa đủ tin cậy qua backtest — chỉ tham khảo, không dùng làm tín hiệu giao dịch độc lập.');
    }
    if (tier === 'consistent') {
      return t('sentiment.psychology.consistentScoreHint', 'Tương quan ổn định với lợi nhuận kỳ vọng — dùng cùng trạng thái tâm lý và xu hướng hành động.');
    }
    if (tier === 'validated') {
      return t('sentiment.psychology.validatedScoreHint', 'Đã kiểm chứng qua backtest đa chỉ số/chu kỳ.');
    }
    return '';
  };

  const summaryItems = signal ? [
    {
      label: signal.trade_date,
      value: stateLabel,
      color: stateStyle.color,
      tooltip: t('sentiment.psychology.latestStateHint', "Latest day's state only \u2014 the state distribution below covers the whole selected period"),
    },
    ...(actionBiasLabel ? [{
      label: t('sentiment.psychology.actionBiasLabel', 'Action Bias'),
      value: actionBiasLabel,
      color: getScoreColor(ACTION_BIAS_LEVELS[signal.action_bias] || 'unknown'),
      tooltip: t(`sentiment.psychology.actionBiasHint.${actionBiasHintKey}`, t(`sentiment.psychology.actionBiasHint.${signal.action_bias}`, '')),
    }] : []),
    ...(exposureLabel ? [{
      label: t('sentiment.psychology.recommendedExposure', 'Exposure Range'),
      value: exposureLabel,
      color: getScoreColor('good'),
      tooltip: t('sentiment.psychology.recommendedExposureHint', 'Suggested portfolio weight range, aligned with action bias'),
    }] : []),
    {
      label: t('sentiment.psychology.opportunityScore', 'Opportunity Score'),
      value: formatScore(signal.opportunity_score),
      color: getScoreColor(signal.opportunity_score_level),
      tooltip: [
        opportunitySourceKey && opportunitySourceKey !== 'minimal'
          ? t(`sentiment.psychology.opportunitySourceHint.${opportunitySourceKey}`, '')
          : null,
        reliabilityHint(signal.reliability?.opportunity_score),
        t('sentiment.psychology.opportunityScoreHint', ''),
      ].filter(Boolean).join(' '),
    },
    {
      label: t('sentiment.psychology.sentimentScore', 'Sentiment Score'),
      value: formatScore(signal.sentiment_score),
      color: getScoreColor(signal.sentiment_score_level),
      tooltip: reliabilityHint(signal.reliability?.psychology_state) || scoreLevelLabel(signal.sentiment_score_level),
    },
    ...(signal.transition_probability != null ? [{
      label: t('sentiment.psychology.transitionProbability', 'Transition Probability'),
      value: formatTransitionValue(signal.transition_probability, signal.transition_direction),
      color: getScoreColor(TRANSITION_DIRECTION_LEVELS[signal.transition_direction] || 'unknown'),
      tooltip: t('sentiment.psychology.transitionProbabilityHint', 'How close the sentiment score is to the nearest state boundary \u2014 \u2191 means closer to a more optimistic neighboring state, \u2193 means closer to a more fearful one'),
    }] : []),
  ] : [];

  const selectedRangeOption = TIMEFRAME_OPTIONS.find((option) => option.days === historyDays);
  const selectedRangeLabel = selectedRangeOption
    ? t(`sentiment.psychology.range.${selectedRangeOption.value.toLowerCase()}`, selectedRangeOption.value)
    : '';

  const rangeSelector = (
    <Box sx={{ position: 'relative', width: 108, height: 26 }}>
      <Select
        data-html2canvas-ignore="true"
        value={historyDays}
        onChange={(e) => onHistoryDaysChange(e.target.value)}
        size="small"
        sx={{
          width: '100%',
          height: '100%',
          fontSize: '0.78rem',
          borderRadius: 0.5,
          display: 'flex',
          justifyContent: 'center',
          '& .MuiSelect-select': {
            py: 0.4, pl: 1.1, pr: '4px !important', flexGrow: 0, flexBasis: 'auto', width: 'auto !important', minWidth: '0 !important', display: 'flex', alignItems: 'center',
          },
          '& .MuiSelect-icon': { position: 'static', ml: 0.2, mr: 0.6 },
          '& .MuiOutlinedInput-notchedOutline': { borderRadius: 0.5 },
        }}
      >
        {TIMEFRAME_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.days} sx={{ fontSize: '0.78rem' }}>
            {t(`sentiment.psychology.range.${option.value.toLowerCase()}`, option.value)}
          </MenuItem>
        ))}
      </Select>
      <Box
        className="export-range-label"
        sx={{
          display: 'none',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.2,
          fontSize: '0.78rem',
          color: 'text.primary',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: 0.5,
          bgcolor: '#fff',
        }}
      >
        <span>{selectedRangeLabel}</span>
        <ArrowDropDownIcon sx={{ fontSize: '1.1rem', color: 'rgba(0, 0, 0, 0.54)' }} />
      </Box>
    </Box>
  );

  return (
    <Card ref={exportRef} sx={{ mb: 2.5, borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)' }}>
      <Box sx={{
        px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap',
      }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: 0.6 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3 }}>
              {t('sentiment.psychology.section_title', 'Market Psychology')} · {indexCode}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.2, lineHeight: 1.35 }}>
              {t('sentiment.psychology.historySubtitle', 'VNINDEX vs. Sentiment Score & Opportunity Score')}
            </Typography>
          </Box>
          <Tooltip title={t('sentiment.psychology.experimentalNote', 'Experimental model')}>
            <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0 }} />
          </Tooltip>
        </Box>

        {!loading && summaryItems.length > 0 && (
          <Box sx={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start',
            rowGap: 1, columnGap: { xs: 1.5, sm: 2 },
            flex: { xs: '1 1 100%', md: '1 1 auto' }, minWidth: 0,
          }}
          >
            {summaryItems.map((item) => (
              <SummaryItem
                key={item.label}
                label={item.label}
                value={item.value}
                color={item.color}
                tooltip={item.tooltip}
              />
            ))}
          </Box>
        )}

        <Box sx={{ flexShrink: 0, ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {refreshing && <CircularProgress size={16} thickness={5} />}
          {rangeSelector}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-html2canvas-ignore="true">
            <Tooltip title={calculating ? t('sentiment.psychology.calculating', 'Calculating...') : t('sentiment.psychology.calculate', 'Recalculate')}>
              <span>
                <IconButton size="small" onClick={onCalculate} disabled={calculating}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={exportingImage ? t('sentiment.psychology.exportingImage', 'Exporting image...') : t('sentiment.psychology.exportImage', 'Export image')}>
              <span>
                <IconButton size="small" onClick={handleExportImage} disabled={exportingImage || loading}>
                  {exportingImage ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {!loading && stateHint && (
        <Box sx={{ px: 2, pb: 0.5 }}>
          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.4 }}>
            {stateHint}
          </Typography>
        </Box>
      )}

      {!loading && warningCodes.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700 }}>
            {t('sentiment.psychology.warningsLabel', 'Alerts')}:
          </Typography>
          {warningCodes.map((code) => (
            <Typography
              key={code}
              sx={{
                fontSize: '0.68rem', fontWeight: 600, px: 0.75, py: 0.2,
                borderRadius: 0.5, bgcolor: 'warning.light', color: 'warning.dark',
              }}
            >
              {t(`sentiment.psychology.warning.${code}`, code)}
            </Typography>
          ))}
        </Box>
      )}

      {!loading && keyDrivers.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 700 }}>
            {t('sentiment.psychology.keyDrivers', 'Why this state')}:
          </Typography>
          {keyDrivers.map((driver) => (
            <Typography
              key={driver.factor}
              sx={{ fontSize: '0.7rem', fontWeight: 700, color: getScoreColor(KEY_DRIVER_LEVELS[driver.direction] || 'unknown') }}
            >
              {t(`sentiment.psychology.driver.${driver.factor}`, driver.factor)}{KEY_DRIVER_ARROWS[driver.direction] || ''}
            </Typography>
          ))}
        </Box>
      )}

      <CardContent sx={{ pt: 0.75, '&:last-child': { pb: 2 } }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && !signal && (
          <Typography color="text.secondary" sx={{ fontSize: '0.85rem' }}>
            {t('sentiment.psychology.noData', 'No data yet')}
          </Typography>
        )}
        {!loading && signal && (
          <Box sx={{ opacity: refreshing ? 0.5 : 1, transition: 'opacity 0.15s ease' }}>
            {chartData.length > 1 ? (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2.5, mb: 1 }}>
                  {[
                    { name: vnindexLabel, color: colors.gray[600], style: 'line' },
                    { name: sentimentLabel, color: sentimentColor, style: 'line' },
                    { name: opportunityLabel, color: opportunityColor, style: 'line' },
                  ].map((entry) => (
                    <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {entry.style === 'area' ? (
                        <Box sx={{ width: 14, height: 8, borderRadius: 0.5, bgcolor: entry.color, opacity: 0.35 }} />
                      ) : (
                        <Box sx={{ width: 14, height: 2.5, borderRadius: 1, bgcolor: entry.color }} />
                      )}
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary' }}>{entry.name}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ height: CHART_HEIGHT }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={chartData}
                      margin={CHART_MARGIN}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={chartAxisTick}
                        minTickGap={28}
                        tickFormatter={formatDateTick}
                        axisLine={{ stroke: '#e2e8f0' }}
                        tickLine={false}
                        height={22}
                      />
                      <YAxis
                        yAxisId="left"
                        domain={vnindexDomain}
                        tick={chartAxisTick}
                        axisLine={false}
                        tickLine={false}
                        width={CHART_LEFT_AXIS_WIDTH}
                        tickFormatter={(v) => `${(v / 1000).toFixed(1)}k`}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        tick={chartAxisTick}
                        axisLine={false}
                        tickLine={false}
                        width={CHART_RIGHT_AXIS_WIDTH}
                      />
                      <ReferenceLine yAxisId="right" y={50} stroke="#d1d5db" strokeDasharray="4 4" />
                      <RTooltip
                        labelFormatter={formatTooltipLabel}
                        formatter={formatTooltipValue}
                        contentStyle={chartTooltipStyle}
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="vnindex"
                        name={vnindexLabel}
                        stroke={colors.gray[600]}
                        fill={colors.gray[600]}
                        fillOpacity={0.06}
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="opportunity"
                        name={opportunityLabel}
                        stroke={opportunityColor}
                        fill={opportunityColor}
                        fillOpacity={0.12}
                        strokeWidth={2.5}
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="sentiment"
                        stroke={sentimentColor}
                        strokeWidth={1.75}
                        strokeOpacity={0.9}
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                        name={sentimentLabel}
                      />
                      <Brush
                        dataKey="date"
                        height={CHART_BRUSH_HEIGHT}
                        gap={3}
                        travellerWidth={8}
                        stroke={colors.gray[500]}
                        fill="#f8fafc"
                        tickFormatter={formatDateTick}
                        startIndex={brushRange.startIndex}
                        endIndex={brushRange.endIndex}
                        onChange={handleBrushChange}
                      >
                        <AreaChart>
                          <Area
                            type="monotone"
                            dataKey={(point) => (point.vnindex != null ? point.vnindex - vnindexPreviewBase : null)}
                            stroke={colors.gray[500]}
                            fill={colors.gray[500]}
                            fillOpacity={0.25}
                            connectNulls
                            isAnimationActive={false}
                          />
                        </AreaChart>
                      </Brush>
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ fontSize: '0.8rem', py: 2 }}>
                {t('sentiment.psychology.noData', 'No data yet')}
              </Typography>
            )}


            {(stats || stats?.states?.length > 0) && (
              <Box sx={{
                display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between',
                alignItems: 'center', columnGap: 3, rowGap: 0.75, mt: 1,
              }}
              >
                {stats && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getScoreColor('good') }} />
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {t('sentiment.psychology.avgSentiment', 'Avg Sentiment')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{formatScore(stats.avg_sentiment_score)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getScoreColor('excellent') }} />
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {t('sentiment.psychology.avgOpportunity', 'Avg Opportunity')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{formatScore(stats.avg_opportunity_score)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {t('sentiment.psychology.avgRisk', 'Avg Risk')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{formatScore(stats.avg_risk_score)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                        {t('sentiment.psychology.totalDays', 'Data Days')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{stats.total_days ?? '—'}</Typography>
                    </Box>
                  </Box>
                )}

                {stats?.states?.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700 }}>
                      {t('sentiment.psychology.stateDistribution', 'State Distribution')}:
                    </Typography>
                    {stats.states.map((s) => {
                      const style = STATE_STYLES[s.psychology_state] || STATE_STYLES.DOUBT;
                      const stateName = t(`sentiment.psychology.state.${s.psychology_state}`, s.psychology_state);
                      return (
                        <Box key={s.psychology_state} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: style.color }} />
                          <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{stateName}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{s.count}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

MarketPsychologySection.propTypes = {
  indexCode: PropTypes.string.isRequired,
  signal: PropTypes.shape({
    trade_date: PropTypes.string,
    sentiment_score: PropTypes.number,
    sentiment_score_level: PropTypes.string,
    market_confirmation: PropTypes.number,
    market_confirmation_level: PropTypes.string,
    opportunity_score: PropTypes.number,
    opportunity_score_level: PropTypes.string,
    risk_score: PropTypes.number,
    risk_score_level: PropTypes.string,
    psychology_state: PropTypes.string,
    transition_probability: PropTypes.number,
    transition_direction: PropTypes.oneOf(['up', 'down']),
    action_bias: PropTypes.string,
    action_bias_hint_key: PropTypes.string,
    opportunity_source: PropTypes.oneOf(['contrarian', 'trend', 'mixed', 'minimal']),
    opportunity_contrarian: PropTypes.number,
    opportunity_trend: PropTypes.number,
    recommended_exposure_min: PropTypes.number,
    recommended_exposure_max: PropTypes.number,
    warnings: PropTypes.arrayOf(PropTypes.string),
    reliability: PropTypes.objectOf(PropTypes.string),
    key_drivers: PropTypes.arrayOf(PropTypes.shape({
      factor: PropTypes.string,
      score: PropTypes.number,
      direction: PropTypes.oneOf(['bullish', 'bearish', 'neutral']),
    })),
  }),
  stats: PropTypes.shape({
    total_days: PropTypes.number,
    avg_sentiment_score: PropTypes.number,
    max_sentiment_score: PropTypes.number,
    min_sentiment_score: PropTypes.number,
    avg_market_confirmation: PropTypes.number,
    avg_opportunity_score: PropTypes.number,
    avg_risk_score: PropTypes.number,
    states: PropTypes.arrayOf(PropTypes.shape({
      psychology_state: PropTypes.string,
      count: PropTypes.number,
    })),
  }),
  history: PropTypes.arrayOf(PropTypes.shape({
    trade_date: PropTypes.string,
    sentiment_score: PropTypes.number,
    opportunity_score: PropTypes.number,
  })),
  vnindexByDate: PropTypes.objectOf(PropTypes.number),
  historyDays: PropTypes.number,
  onHistoryDaysChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  refreshing: PropTypes.bool,
  calculating: PropTypes.bool,
  onCalculate: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

MarketPsychologySection.defaultProps = {
  signal: null,
  stats: null,
  history: [],
  vnindexByDate: {},
  historyDays: 180,
  loading: false,
  refreshing: false,
  calculating: false,
};

export default MarketPsychologySection;
